import { Download, Eye, LoaderCircle, Printer, Share2 } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { getError } from "@/api";

const saveBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 5000);
};

export const PdfFileActions = ({ fileName, getPdfBlob, testIdPrefix }) => {
  const [workingAction, setWorkingAction] = useState("");
  const cachedPdf = useRef({ blob: null, fileName: "", fetcher: null, pending: null });

  const loadPdf = async () => {
    const cache = cachedPdf.current;

    if (cache.blob && cache.fileName === fileName && cache.fetcher === getPdfBlob) {
      return cache.blob;
    }

    if (cache.pending && cache.fileName === fileName && cache.fetcher === getPdfBlob) {
      return cache.pending;
    }

    const pending = getPdfBlob();
    cachedPdf.current = { blob: null, fileName, fetcher: getPdfBlob, pending };

    try {
      const blob = await pending;
      cachedPdf.current = { blob, fileName, fetcher: getPdfBlob, pending: null };
      return blob;
    } catch (error) {
      cachedPdf.current = { blob: null, fileName: "", fetcher: null, pending: null };
      throw error;
    }
  };

  const preload = () => {
    if (!workingAction) {
      loadPdf().catch(() => {});
    }
  };

  const withPdf = async (action, callback) => {
    if (workingAction) {
      return;
    }

    setWorkingAction(action);

    try {
      const blob = await loadPdf();
      await callback(blob);
    } catch (error) {
      if (error.name !== "AbortError") {
        toast.error(getError(error));
      }
    } finally {
      setWorkingAction("");
    }
  };

  const download = () => withPdf("download", async (blob) => {
    saveBlob(blob, fileName);
    toast.success("Téléchargement PDF lancé.");
  });

  const view = () => withPdf("view", async (blob) => {
    const url = URL.createObjectURL(blob);
    const previewWindow = window.open(url, "_blank", "noopener,noreferrer");

    if (!previewWindow) {
      saveBlob(blob, fileName);
      toast.info("Aperçu indisponible : le PDF a été téléchargé.");
      return;
    }

    window.setTimeout(() => URL.revokeObjectURL(url), 30000);
  });

  const print = () => withPdf("print", async (blob) => {
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, "_blank", "noopener,noreferrer");

    if (!printWindow) {
      saveBlob(blob, fileName);
      toast.info("Impression indisponible : le PDF a été téléchargé.");
      return;
    }

    printWindow.addEventListener("load", () => printWindow.print(), { once: true });
    window.setTimeout(() => URL.revokeObjectURL(url), 30000);
  });

  const share = () => withPdf("share", async (blob) => {
    const file = new File([blob], fileName, { type: "application/pdf" });

    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ title: "Document de stock", files: [file] });
      toast.success("Document prêt à être partagé.");
      return;
    }

    saveBlob(blob, fileName);
    toast.info("Partage non disponible : le PDF a été téléchargé.");
  });

  return (
    <div
      className="pdf-file-actions"
      data-testid={`${testIdPrefix}-pdf-actions`}
      onFocus={preload}
      onPointerEnter={preload}
    >
      <button
        aria-label="Visualiser le PDF"
        data-testid={`${testIdPrefix}-view-button`}
        disabled={Boolean(workingAction)}
        onClick={view}
        title="Visualiser"
        type="button"
      >
        {workingAction === "view" ? <LoaderCircle className="dashboard-spinner" size={16} /> : <Eye size={16} />}
        <span>Visualiser</span>
      </button>
      <button
        aria-label="Imprimer le PDF"
        data-testid={`${testIdPrefix}-print-button`}
        disabled={Boolean(workingAction)}
        onClick={print}
        title="Imprimer"
        type="button"
      >
        {workingAction === "print" ? <LoaderCircle className="dashboard-spinner" size={16} /> : <Printer size={16} />}
        <span>Imprimer</span>
      </button>
      <button
        aria-label="Télécharger le PDF"
        data-testid={`${testIdPrefix}-download-button`}
        disabled={Boolean(workingAction)}
        onClick={download}
        title="Télécharger en PDF"
        type="button"
      >
        {workingAction === "download" ? <LoaderCircle className="dashboard-spinner" size={16} /> : <Download size={16} />}
        <span>PDF</span>
      </button>
      <button
        aria-label="Partager le PDF"
        data-testid={`${testIdPrefix}-share-button`}
        disabled={Boolean(workingAction)}
        onClick={share}
        title="Partager le PDF"
        type="button"
      >
        {workingAction === "share" ? <LoaderCircle className="dashboard-spinner" size={16} /> : <Share2 size={16} />}
        <span>Partager</span>
      </button>
    </div>
  );
};